import _, { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectSelectedDateValue,
  useReportStore,
} from '@/entities/report';
import { logError } from '@/shared/lib/apiHelpers';
import { HR_TYPE, isOutOfRangeHr } from '@/widgets/hr-summary/ui/helper';
import type { HrChartOptionsProps } from './index';

/**
 * Drives the HR summary side panel where a reviewer inspects and overrides the min, avg and max heart rate of the selected day. Mirrors the store defaults into local state while preserving in-progress edits whose timestamps still match, builds the three rendered rows with their view and save buttons, jumps the chart to the min or max beat on view, and writes accepted edits into the updated daily summaries.
 *
 * @param props - HR chart options props, carrying the study timezoneOffset.
 * @returns An object carrying isLoadingHrChart, isEmptyData, renderHrItems, onClickCancel and onChangeValue.
 */
const useHrChartOptions = ({ timezoneOffset }: Pick<HrChartOptionsProps, 'timezoneOffset'>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  // hrDataDefaultState/minHrsInDateState/maxHrsInDateState (Recoil) are typed
  // IHrSummary/IHrsInDate[], missing isEdited/isError/hrsOnBeat/userUpdated
  // fields this component reads and writes locally - pre-existing gap.
  const hrDataDefault: any = useReportStore((s) => s.hrDataDefault);
  const minHrsInDate: any = useReportStore((s) => s.minHrsInDate);
  const maxHrsInDate: any = useReportStore((s) => s.maxHrsInDate);
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the report to another date on behalf of the HR summary tab. Forwards the value to the store action together with the tab key.
   *
   * @param v - The jump date value accepted by the store action.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setSelectedHrTimeAction = useReportStore((s) => s.actions.setSelectedHrTime);
  /**
   * Marks which heart rate timestamp the HR summary tab is focused on. Forwards the value to the store action together with the tab key.
   *
   * @param v - Timestamp of the focused heart rate, or undefined to clear it.
   * @returns Nothing.
   */
  const setSelectedHrTime = (v: string | undefined) => setSelectedHrTimeAction(keyRecoil, v);
  const setViewHrModeAction = useReportStore((s) => s.actions.setViewHrMode);
  /**
   * Switches the HR summary tab between reviewing the minimum and the maximum heart rate. Forwards the mode to the store action together with the tab key.
   *
   * @param v - Either the MAX_HR or the MIN_HR rhythm event mode.
   * @returns Nothing.
   */
  const setViewHrMode = (v: TYPE_RHYTHM_EVENT_ENUM.MAX_HR | TYPE_RHYTHM_EVENT_ENUM.MIN_HR) =>
    setViewHrModeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Moves the viewer to a portion within the selected day on behalf of the HR summary tab. Forwards the value to the store action together with the tab key.
   *
   * @param v - The jump portion value accepted by the store action.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  /**
   * Reads the date currently selected on the HR summary tab. Applies the tab-keyed selectSelectedDateValue selector to a getState snapshot instead of subscribing the hook.
   *
   * @returns The selected date value for the HR summary tab.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  /**
   * Reads the locally edited daily HR summaries straight from the report store. Pulls them from a getState snapshot so the save handler always works against the latest list.
   *
   * @returns The edited daily HR summaries held in the report store.
   */
  const getUpdatedDailySummaries = () => useReportStore.getState().updatedDailySummaries;
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  /**
   * Reads the server-loaded daily HR summaries straight from the report store. Pulls them from a getState snapshot so the save handler sees current values without subscribing the hook to them.
   *
   * @returns The original daily HR summaries held in the report store.
   */
  const getOriginalDailySummaries = () => useReportStore.getState().originalDailySummaries;
  const setMinHrTabActive = useReportStore((s) => s.actions.setMinHrTabActive);

  const [hrData, setHrData] = useState<any>(
    cloneDeep(hrDataDefault) || { minHr: {}, maxHr: {}, avgHr: {} },
  );

  useEffect(() => {
    setHrData((prevHrData: any) => {
      const updatedHrData = _.cloneDeep(hrDataDefault);
      // Preserve edited values if time matches
      if (prevHrData.minHr?.time === hrDataDefault.minHr?.time) {
        updatedHrData.minHr.isEdited = prevHrData.minHr.isEdited;
        updatedHrData.minHr.value = prevHrData.minHr.isEdited
          ? prevHrData.minHr.value
          : hrDataDefault.minHr?.value;
      }
      if (prevHrData.maxHr?.time === hrDataDefault.maxHr?.time) {
        updatedHrData.maxHr.isEdited = prevHrData.maxHr.isEdited;
        updatedHrData.maxHr.value = prevHrData.maxHr.isEdited
          ? prevHrData.maxHr.value
          : hrDataDefault.maxHr?.value;
      }
      if (prevHrData.avgHr?.time === hrDataDefault.avgHr?.time) {
        updatedHrData.avgHr.isEdited = prevHrData.avgHr.isEdited;
        updatedHrData.avgHr.value = prevHrData.avgHr.isEdited
          ? prevHrData.avgHr.value
          : hrDataDefault.avgHr?.value;
      }

      return updatedHrData;
    });
  }, [hrDataDefault]);

  /**
   * Reverts the edit state of one heart rate field in the HR summary options. Clears isEdited and isError on the min, max or avg field and restores the stored default value unless isValueOld is false.
   *
   * @param type - Which HR field to revert, one of the HR_TYPE values.
   * @param isValueOld - Whether the default value is restored as well, true by default.
   * @returns Nothing.
   */
  const onClickCancel = (type: string, isValueOld = true) => {
    setHrData((prevHrData: any) => {
      const updatedHrData = _.cloneDeep(prevHrData);
      if (type === HR_TYPE.MIN_HR) {
        updatedHrData.minHr.isEdited = false;
        updatedHrData.minHr.isError = false;
        if (isValueOld) {
          updatedHrData.minHr.value = hrDataDefault.minHr?.value;
        }
      } else if (type === HR_TYPE.MAX_HR) {
        updatedHrData.maxHr.isEdited = false;
        updatedHrData.maxHr.isError = false;
        if (isValueOld) {
          updatedHrData.maxHr.value = hrDataDefault.maxHr?.value;
        }
      } else if (type === HR_TYPE.AVG_HR) {
        updatedHrData.avgHr.isEdited = false;
        updatedHrData.avgHr.isError = false;
        if (isValueOld) {
          updatedHrData.avgHr.value = hrDataDefault.avgHr?.value;
        }
      }
      return updatedHrData;
    });
  };

  // This function is used to check if the hr value is out of range and save the hr data
  /**
   * Commits the edited heart rates of the selected day into the updated daily summaries. Aborts on an out-of-range or blank value, builds min and max entries with the timezone-converted time, the matching hrsOnBeat window and a userUpdated flag, rebuilds suggestHrsOnBeat from the per-day HR lists, upserts the summary by date and clears the edited flags, logging and toasting any failure.
   *
   * @param type - Which HR field triggered the save, one of the HR_TYPE values.
   * @returns Nothing.
   */
  const onClickSave = (type: string) => {
    try {
      const { minHr: minHrDefault, maxHr: maxHrDefault, avgHr: avgHrDefault } = hrDataDefault;
      const { minHr, maxHr, avgHr } = hrData;
      if (
        isOutOfRangeHr(minHr.value) ||
        isOutOfRangeHr(maxHr.value) ||
        isOutOfRangeHr(parseFloat(avgHr.value))
      ) {
        return;
      }
      const originalDailySummariesValue = getOriginalDailySummaries();
      const originalDailySummariesValueInDate = originalDailySummariesValue.find(
        (x) => x.date === getSelectedDateValue(),
      );

      const isChangedMinHr =
        Number(minHr.value) !== minHrDefault.value || minHr.time !== minHrDefault.time;
      const isChangedMaxHr =
        Number(maxHr.value) !== maxHrDefault.value || maxHr.time !== maxHrDefault.time;
      const isChangedAvgHr = Number(avgHr.value) !== avgHrDefault.value;
      const selectedDateValue = getSelectedDateValue();

      if (isChangedMinHr || isChangedMaxHr || isChangedAvgHr) {
        // if minHR or maxHR or avgHR is undefined or '' then return
        if (minHr.value === undefined || minHr.value === '') return;
        if (maxHr.value === undefined || maxHr.value === '') return;
        if (avgHr.value === undefined || avgHr.value === '') return;
        let minHrUpdated;
        let maxHrUpdated;
        if (type === HR_TYPE.MIN_HR) {
          const foundItem = _.find(minHrsInDate, (x) => x.start === minHr.time);
          minHrUpdated = {
            time: convertToMomentJs(minHr?.time, timezoneOffset).toISOString(),
            value: Number(minHr.value),
            hrsOnBeat: {
              head: foundItem?.start,
              tail: foundItem?.stop,
            },
            userUpdated: Number(minHr.value) !== minHrDefault.value,
          };
        } else {
          minHrUpdated = {
            time: minHrDefault.time,
            value: minHrDefault.value,
            hrsOnBeat: minHrDefault.hrsOnBeat,
            userUpdated: minHrDefault.userUpdated,
          };
        }
        if (type === HR_TYPE.MAX_HR) {
          const foundItem = _.find(maxHrsInDate, (x) => x.start === maxHr.time);
          maxHrUpdated = {
            time: convertToMomentJs(maxHr?.time, timezoneOffset).toISOString(),
            value: Number(maxHr.value),
            hrsOnBeat: {
              head: foundItem?.start,
              tail: foundItem?.stop,
            },
            userUpdated: Number(maxHr.value) !== maxHrDefault.value,
          };
        } else {
          maxHrUpdated = {
            time: maxHrDefault.time,
            value: maxHrDefault.value,
            hrsOnBeat: maxHrDefault.hrsOnBeat,
            userUpdated: maxHrDefault.userUpdated,
          };
        }
        const updatedDailySummary = {
          date: selectedDateValue,
          minHr: minHrUpdated,
          maxHr: maxHrUpdated,
          avgHr: {
            value: type === HR_TYPE.AVG_HR ? Number(avgHr.value) : avgHrDefault.value,
          },
          suggestHrsOnBeat: {
            maxHr: maxHrsInDate.map((item: any) => {
              if (item.start === maxHrUpdated.time) {
                return {
                  head: item.start,
                  tail: item.stop,
                  avgHr: maxHrUpdated.value,
                  userUpdated: item.value !== maxHrUpdated.value ? true : item.userUpdated,
                };
              }
              return {
                head: item.start,
                tail: item.stop,
                avgHr: item.value,
                userUpdated: item.userUpdated,
              };
            }),
            minHr: minHrsInDate.map((item: any) => {
              if (item.start === minHrUpdated.time) {
                return {
                  head: item.start,
                  tail: item.stop,
                  avgHr: minHrUpdated.value,
                  userUpdated: item.value !== minHrUpdated.value ? true : item.userUpdated,
                };
              }
              return {
                head: item.start,
                tail: item.stop,
                avgHr: item.value,
                userUpdated: item.userUpdated,
              };
            }),
          },
        };
        const cloneUpdatedDailySummaries = _.cloneDeep(getUpdatedDailySummaries());
        const foundDailySummary = cloneUpdatedDailySummaries.find(
          (x) => x.date === selectedDateValue,
        );
        if (foundDailySummary) {
          _.assign(foundDailySummary, updatedDailySummary);
        } else {
          cloneUpdatedDailySummaries.push(updatedDailySummary);
        }
        setUpdatedDailySummaries(cloneUpdatedDailySummaries);
      }
      onClickCancel(type, false);
    } catch (error) {
      logError('Failed to update holter HR daily summary: ', error);
      toastrError(error.message, 'Error');
    }
  };

  // This function is used to view the max hr
  /**
   * Jumps the HR chart and ECG viewer to the day's maximum heart rate beat. Does nothing without a stored max time, otherwise switches the view mode to max, points the selected HR time and jump date at that timestamp, resets the jump portion and leaves the min HR tab inactive.
   *
   * @returns Nothing.
   */
  const onClickViewMaxHr = () => {
    if (hrDataDefault.maxHr?.time) {
      setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MAX_HR);
      setSelectedHrTime(hrDataDefault.maxHr.time);
      setJumpDate(hrDataDefault.maxHr.time);
      setJumpPortion(0);
      setMinHrTabActive(false);
    }
  };

  // This function is used to view the min hr
  /**
   * Jumps the HR chart and ECG viewer to the day's minimum heart rate beat. Does nothing without a stored min time, otherwise switches the view mode to min, points the selected HR time and jump date at that timestamp, resets the jump portion and activates the min HR tab.
   *
   * @returns Nothing.
   */
  const onClickViewMinHr = () => {
    if (hrDataDefault.minHr?.time) {
      setViewHrMode(TYPE_RHYTHM_EVENT_ENUM.MIN_HR);
      setSelectedHrTime(hrDataDefault.minHr.time);
      setJumpDate(hrDataDefault.minHr.time);
      setJumpPortion(0);
      setMinHrTabActive(true);
    }
  };

  // This function is used to check if the hr value is out of range
  const renderHrItems = React.useMemo(
    () => [
      {
        title: 'Min HR:',
        value: hrData.minHr?.value,
        type: HR_TYPE.MIN_HR,
        time: convertToMomentJs(hrData.minHr?.time, timezoneOffset).format('HH:mm:ss'),
        isError: hrData.minHr.isError,
        viewBtn: {
          text: 'View',
          onClick: onClickViewMinHr,
        },
        divider: true,
        disabled: hrDataDefault.minHr.value === undefined,
        isUserUpdated: hrData.minHr.userUpdated,
        isEdited: hrData.minHr.isEdited,
        placeholder: hrDataDefault.minHr.value === undefined ? 'NA' : 'Enter HR',
        saveBtn: {
          onClick: () => onClickSave(HR_TYPE.MIN_HR),
        },
      },
      {
        title: 'Avg HR:',
        value: hrData.avgHr?.value,
        type: HR_TYPE.AVG_HR,
        isError: hrData.avgHr.isError,
        isUserUpdated: hrData.avgHr?.userUpdated,
        isEdited: hrData.avgHr.isEdited,
        disabled: hrDataDefault.avgHr.value === undefined,
        placeholder: hrDataDefault.avgHr.value === undefined ? 'NA' : 'Enter HR',
        saveBtn: {
          onClick: () => onClickSave(HR_TYPE.AVG_HR),
        },
      },
      {
        title: 'Max HR: ',
        value: hrData.maxHr?.value,
        type: HR_TYPE.MAX_HR,
        time: convertToMomentJs(hrData.maxHr?.time, timezoneOffset).format('HH:mm:ss'),
        isError: hrData.maxHr.isError,
        isEdited: hrData.maxHr.isEdited,
        viewBtn: {
          text: 'View',
          onClick: onClickViewMaxHr,
        },
        divider: true,
        disabled: hrDataDefault.maxHr.value === undefined,
        isUserUpdated: hrData.maxHr?.userUpdated,
        placeholder: hrDataDefault.maxHr.value === undefined ? 'NA' : 'Enter HR',
        saveBtn: {
          onClick: () => onClickSave(HR_TYPE.MAX_HR),
        },
      },
    ],
    [hrData, timezoneOffset],
  );

  // This function is used to check if the hr value is out of range
  /**
   * Applies a typed heart rate into the local min, max or avg field and flags its edited and error state. Ignores a value equal to the stored default, coerces the input with Number, stores undefined for zero, sets isEdited by comparing against the default and isError from the 25 to 300 bpm range check.
   *
   * @param _value - Raw heart rate text entered by the reviewer.
   * @param type - Which HR field is being edited, one of the HR_TYPE values.
   * @returns Nothing.
   */
  const onChangeValue = (_value: any, type: string) => {
    const value = Number(_value);
    if (value === hrDataDefault[type]?.value) {
      return;
    }
    const cloneHrData = _.cloneDeep(hrData);
    const { minHr: minHrDefault, maxHr: maxHrDefault, avgHr: avgHrDefault } = hrDataDefault;
    if (type === HR_TYPE.MAX_HR) {
      cloneHrData.maxHr.value = value === 0 ? undefined : value;
      cloneHrData.maxHr.isEdited = value !== maxHrDefault.value && maxHrDefault.value !== undefined;
      cloneHrData.maxHr.isError = isOutOfRangeHr(value) && maxHrDefault.value !== undefined;
    } else if (type === HR_TYPE.MIN_HR) {
      cloneHrData.minHr.value = value === 0 ? undefined : value;
      cloneHrData.minHr.isEdited = value !== minHrDefault.value && minHrDefault.value !== undefined;
      cloneHrData.minHr.isError = isOutOfRangeHr(value) && minHrDefault.value !== undefined;
    } else {
      cloneHrData.avgHr.value = value === 0 ? undefined : value;
      cloneHrData.avgHr.isEdited = value !== avgHrDefault.value && avgHrDefault.value !== undefined;
      cloneHrData.avgHr.isError = isOutOfRangeHr(value) && avgHrDefault.value !== undefined;
    }
    setHrData(cloneHrData);
  };

  return {
    isLoadingHrChart,
    isEmptyData,
    renderHrItems,
    onClickCancel,
    onChangeValue,
  };
};

export default useHrChartOptions;
